import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CommonHttpService } from 'src/app/shared/services/common-http/common-http.service';
import { devicePixelRatio, DefaultLanguageId, linkPattern } from 'src/app/shared/constants';
import { ActivatedRoute } from '@angular/router';
import { Resource } from 'src/app/shared/model/resource-model';
import {
  resourceAllowedExtension,
  videoAllowedExtension,
  imageAllowedExtension
} from 'src/app/shared/constants';
import { includes } from 'lodash';
import { MessageService } from 'src/app/shared/services/message/message.service';
import { CustomTypeFilter, FilterOptions } from 'src/app/shared/interfaces/filter-options';
import { PlaybookCommunicationService } from 'src/app/playbook-editor/shared/services/playbook-communication.service';

@Component({
    selector: 'app-add-edit-resource',
    templateUrl: './add-edit-resource.component.html',
    styleUrls: ['./add-edit-resource.component.scss'],
    standalone: false
})
export class AddEditResourceComponent implements OnInit {

  // tslint:disable-next-line: max-line-length
  resourceData: any;
  containerId: number;
  resourceCount: number;
  isUpdate: boolean;
  profileId: number;
  imageName: any;
  uploadResourceForm: UntypedFormGroup;
  selectedFile: any;
  playbookId: number;
  languageId: number;
  defaultLanguageId = DefaultLanguageId;
  dataSubmitted = false;
  errMessage: string;
  pageTemplateId;
  progressbarDisplay = {
    image: false,
    asset: false
  };
  dynamicProgressbar = {
    image: null,
    asset: null
  };
  max = 100;
  videoProcessing: string;
  resourceAllowedExtensions = resourceAllowedExtension;
  videoAllowedExtensions = videoAllowedExtension;
  imageAllowedExtensions = imageAllowedExtension;
  devicePixelRatio = devicePixelRatio;
  isCopyExists = false;
  customTypeFilter: CustomTypeFilter;
  dropDownExtraClass = 'notification-dropdown ';
  selectedFolder = { id: null, label: 'None' };
  resourcefolders;
  @ViewChild('fileInputResource') fileInputResource: ElementRef;
  disableSaveBtn = false;
  apiUrl = '/api/Assets/UploadVideoFileAsChunk';
  isDropDownDisabled = false;
  pageFrom;
  constructor(
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private messageService: MessageService,
    private modalRef: BsModalRef,
    private commonHttpService: CommonHttpService,
    private commService: PlaybookCommunicationService,
  ) { }

  ngOnInit() {
    this.languageId = +this.activatedRoute.snapshot.queryParams.languageId || this.defaultLanguageId;
    this.isDropDownDisabled = this.languageId !== this.defaultLanguageId;
    if (!this.resourceData) {
      this.resourceData = new Resource();
      this.resourceData.type = 0;
    }
    this.customTypeFilter = new CustomTypeFilter();
    this.customTypeFilter.id = 'id';
    this.customTypeFilter.label = 'name';
    this.customTypeFilter.model = this.resourcefolders.map(folder => {
      return {
        id: folder.id,
        name: folder.name
      };
    });

    this.createForm();
    this.changeValidation();

    // On create resource check if the profile has copied resource or not.
    if (!this.pageTemplateId && !this.isUpdate) {
      this.checkIfCopyExists();
    }
  }
  
  get form() {
    return this.uploadResourceForm.controls;
  }

  /**
   * To clear the file input resource
   */
  clearResource() {
    if (this.fileInputResource) {
      this.fileInputResource.nativeElement.value = null;
    }
  }

  /**
   * To initialize the resource form
   */
  createForm() {
    this.uploadResourceForm = this.formBuilder.group({
      title: [this.resourceData.title || null, [Validators.required, Validators.maxLength(100)]],
      linkUrl: [this.resourceData.linkUrl || null, [Validators.pattern(linkPattern)]]
    });

  }

  /**
   * To close the resource modal
   */
  closeModal() {
    this.modalRef.hide();
    this.resourceData = null;
  }

  /**
   * To manage validation conditions based on different resource types
   */
  changeValidation() {
    const linkUrl = this.uploadResourceForm.get('linkUrl');
    if (this.resourceData.type === 1) {
      linkUrl.setValidators([Validators.required, Validators.pattern(linkPattern)]);
    } else {
      linkUrl.clearValidators();
    }
    linkUrl.updateValueAndValidity();

  }

  /**
   * To handle switching of resource types
   * @param resourceType changed resource type value
   */
  changeResource(resourceType) {
    this.resourceData.type = resourceType;
    this.changeValidation();
    this.assignNull();
  }

  /**
   * To assign null values to resource fields when changing resource types
   */
  assignNull() {
    this.progressbarDisplay = {
      image: false,
      asset: false
    };
    this.resourceData.asset = null;
    this.resourceData.assetId = null;
  }

  /**
   * To validate extension of uploaded file
   * @param extensionAllowedArray allowed extension values
   */
  checkExtensionFunction(extensionAllowedArray) {
    const extensionArray = this.selectedFile.name.split('.');
    const extension = extensionArray[extensionArray.length - 1].toLowerCase();
    const checkExtension = includes(extensionAllowedArray, extension);
    return checkExtension;
  }

  /**
   * To handle file change event for file and images
   * @param event $event
   * @param extensionAllowedArray allowed extension values
   * @param placeHolder flag for placeholder image
   */
  fileChangeEvent(event: any, extensionAllowedArray: Array<any>): void {
    if (event.target.files[0] !== undefined) {
      this.selectedFile = event.target.files[0];
      this.imageName = event.target.files[0].name;
      const checkExtension = this.checkExtensionFunction(extensionAllowedArray);
      if (checkExtension) {
        this.upload();
      } else {
        const isAre = extensionAllowedArray.length > 1 ? 'are' : 'is';
        const extensionErrorMessage = 'This is not a valid extension. Supported extension(s) '
          + isAre + ' : ' + extensionAllowedArray.join(',') + '.';
        this.messageService.showErrorMessage(extensionErrorMessage);
      }
    }
  }

  /**
   * To handle upload of file or images
   */
  upload() {
    const formData: FormData = new FormData();
    formData.append('file', this.selectedFile);
    this.progressbarDisplay.image = true;
    this.dynamicProgressbar.image = 0;
    this.resourceData.asset = null;
    this.commonHttpService.postWithProgress({
      uri: '/api/Assets/UploadResource',
      object: formData
    }).subscribe((response: any) => {
      if (typeof response === 'number') {
        // To display progress bar while file is being uploaded
        this.dynamicProgressbar.image = response;
      } else if (response) {
        this.progressbarDisplay.image = false;
        this.resourceData.asset = response;
        this.resourceData.assetId = response.id;
        this.resourceData.uRL = '/api/Assets/Resource/' + response.id;
      }
    });
  }

  progressPercentage(progress) {
    this.progressbarDisplay.asset = true;
    this.resourceData.asset = null;
    if (typeof progress.percentage === 'number') {
      this.dynamicProgressbar.asset = progress.percentage;
      this.videoProcessing = progress.text;
    } else {
      this.dynamicProgressbar.asset = 100;
      this.videoProcessing = progress.percentage;
    }
  }

  fileStatusUpdate(fileStatus) {
    this.progressbarDisplay.asset = false;
    if(fileStatus.status) {
      this.resourceData.asset = fileStatus.assetData;
      this.resourceData.assetId = fileStatus.assetData.id;
      this.resourceData.uRL = '/api/Assets/Resource/' + fileStatus.assetData.id;
    }
  }

  /**
   * To check required conditions before saving or updating data
   */
  validateData() {
    this.dataSubmitted = true;
    if (this.uploadResourceForm.valid) {
      if (this.resourceData.type != null && this.resourceData.type !== 1) {
        if (this.resourceData.assetId) {
          this.saveData();
        }
      } else if (this.resourceData.type != null && this.resourceData.type === 1) {
        this.saveData();
      }
    }
    this.checkError();
  }

  /**
   * To display error when required file is not uploaded
   */
  checkError() {
    if (!this.resourceData.assetId && this.resourceData.type !== 1) {
      this.errMessage = 'File Upload is required to add new Resource';
    }
  }

  /**
   * To save or update data
   */
  saveData() {
    // To handle resource order
    const resOrder = (this.resourceData.orderBy || this.resourceData.orderBy === 0) ? this.resourceData.orderBy : this.resourceCount;
    this.modalRef.content.originalResource = this.resourceData;
    this.disableSaveBtn = true;
    
    const resObj = {
      containerId: this.containerId,
      type: this.resourceData.type,
      title: this.uploadResourceForm.value.title,
      assetId: this.resourceData.assetId,
      linkUrl: this.uploadResourceForm.value.linkUrl,
      orderBy: resOrder,
      profileId: this.profileId,
      playbookId: this.playbookId,
      folderId: this.selectedFolder.id,
      pageTemplateId: this.pageTemplateId
    };

    if (this.isUpdate) {
      // To update existig resource
      const updateResObj = { ...resObj, id: this.resourceData.id, languageId: this.languageId };
      this.commonHttpService.put({
        uri: '/api/Resources/UpdateResource',
        object: updateResObj
      }).subscribe((response) => {
        this.modalRef.content.updatedResource = response;
        this.closeModal();
        this.disableSaveBtn = false;
      });
    } else {
      // To add new resource
      this.commonHttpService.post({
        uri: '/api/Resources/AddResource',
        object: resObj
      }).subscribe((response) => {
        response['folderId'] = resObj.folderId;
        this.modalRef.content.addedResource = response;

        this.closeModal();
        this.disableSaveBtn = false;
      });
    }
  }

  /**
   * To open the resource on new page for download
   */
  openResource(resource: Resource) {
    return `${window.location.origin}/assets/download/${resource.assetId}${this.pageFrom ? '?pageFrom=' + this.pageFrom : ''}`;
  }

  /**
   * Copy resource.
   * @param resourceId : Resource id
   */
  copyResource(resourceId) {
    const reqObj = {
      resourceId,
      playbookId: this.playbookId,
      pageTemplateId: this.pageTemplateId
    };

    this.commonHttpService.post({
      uri: '/api/Resources/CopyResource',
      object: reqObj,
      message: {
        successMessage: 'Resource has been copied successfully',
        errorMessage: 'Something went wrong while copying resource. Please try again later.'
      }
    }).subscribe((response) => {
    });
  }

  /**
   * Check if copy resource exists or not.
   */
  checkIfCopyExists() {
    this.isCopyExists = false;

    this.commonHttpService.get({
      uri: `/api/Resources/CheckIfCopyResourceExists?playbookId=${this.playbookId}&pageTemplateId=${this.pageTemplateId}`,
    }).subscribe((response) => {
      if (response && response == 'true') {
        this.isCopyExists = true;
      }
    })
  }

  /**
   * Paste copied resource.
   */
  pasteResource() {
    const reqObj = {
      playbookId: this.playbookId,
      pageId: this.containerId,
      pageTemplateId: this.pageTemplateId
    };

    this.commonHttpService.post({
      uri: '/api/Resources/PasteResource',
      object: reqObj,
      message: {
        errorMessage: "Something went wrong while paste the resource.Please try again later.",
        successMessage: "Resource has been paste successfully."
      }
    }).subscribe((response) => {
      this.modalRef.content.addedResource = response;
      this.closeModal();
      this.commService.setUpdateLanguagesAfterPasteContent();
    });
  }

  setSelectedCategory(option: FilterOptions) {
    this.selectedFolder = option;
  }

  getSelectedCategory() {
    return this.selectedFolder && this.selectedFolder.label || 'None';
  }

  updateFolderName(event) {
    const selectedFolder = this.resourcefolders.find(folder => folder.id == event.id);
    this.commonHttpService.put({
      uri: `/api/Resources/updateResourceFolder`,
      object: {
        id: selectedFolder.id,
        playbookId: selectedFolder.playbookId,
        pageId: selectedFolder.pageId,
        name: event.label,
        order: selectedFolder.order
      }
    }).subscribe((resp) => {
      selectedFolder.name = event.label;
    });
  }
  addFolder(folderName) {
    this.commonHttpService.post({
      uri: `/api/Resources/AddResourceFolder`,
      object: {
        playbookId: this.playbookId,
        pageId: this.containerId,
        name: folderName,
      }
    }).subscribe((resp) => {

      this.resourcefolders.push(resp);
      this.customTypeFilter = new CustomTypeFilter();
      this.customTypeFilter.id = 'id';
      this.customTypeFilter.label = 'name';
      this.customTypeFilter.model = this.resourcefolders.map(folder => {
        return {
          id: folder.id,
          name: folder.name
        };
      });
    });
  }
}
